import {
  VestingGraphData,
  VestingGraphPointGranularity,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import {
  calendarTimeOf,
  findTimestampIndex,
  getTimestampPosition,
  isEventLayoutPoint,
  layoutTimestampForDate,
} from './vesting-graph.geometry';
import {
  LayoutChartPoint,
  MilestoneSegmentBound,
  VestingGraphAxisTick,
  VestingGraphAxisTickLabel,
  VestingGraphEventMarkerStack,
} from './vesting-graph.types';

/** Last schedule payment — never acceleration / expiry, and never a rewritten full-vest date. */
const resolveLastVestingDate = (vestingGraph: VestingGraphData) =>
  [...vestingGraph.points].reverse().find((point) => point.periodShares > 0)?.date;

const findLastVestingColumn = (chartPoints?: LayoutChartPoint[]) =>
  chartPoints
    ?.slice()
    .reverse()
    .find(
      (point) =>
        !point.isAccelerationColumn && !point.isExpiryColumn && (point.periodShares ?? 0) > 0,
    );

/** Acceleration on or before the last vest owns that event — Fully Vested stays off the axis. */
const shouldCaptionFullyVested = ({
  accelerationDate,
  lastVestingDate,
}: {
  accelerationDate?: string;
  lastVestingDate?: string;
}) => {
  if (!lastVestingDate) return false;
  if (!accelerationDate) return true;

  return new Date(accelerationDate).getTime() > new Date(lastVestingDate).getTime();
};

const collectAxisTickCandidates = (vestingGraph: VestingGraphData) => {
  const lastVestingDate = resolveLastVestingDate(vestingGraph);
  const candidates: Array<{ date: string; label: null | VestingGraphAxisTickLabel }> = [
    { date: vestingGraph.startDate, label: 'GRANT_START' },
  ];

  if (
    lastVestingDate &&
    shouldCaptionFullyVested({
      accelerationDate: vestingGraph.accelerationDate,
      lastVestingDate,
    })
  ) {
    candidates.push({ date: lastVestingDate, label: 'FULLY_VESTED' });
  }

  // Today is a cursor, not a payout. It yields to the payment date on that column.
  candidates.push({ date: vestingGraph.asOfDate, label: 'TODAY' });

  if (vestingGraph.cliff) {
    candidates.push({ date: vestingGraph.cliff.endDate, label: 'CLIFF' });
  }

  if (vestingGraph.granularity === VestingGraphPointGranularity.MILESTONE) {
    // Milestone schedules name each target above the plot, so the axis only needs the date.
    candidates.push(...vestingGraph.points.map((point) => ({ date: point.date, label: null })));
  } else {
    // Monthly charts plot every month, but the axis only names the months that actually
    // release shares — the frequency steps. Months inside a step that release nothing are
    // skipped, and crowded neighbours are dropped later by the gap check.
    candidates.push(
      ...vestingGraph.points
        .filter((point) => point.periodShares > 0)
        .map((point) => ({ date: point.date, label: null })),
    );
  }

  return candidates;
};

/** Acceleration and expiry are dedicated skinny columns — ticks must not sit on them. */
const collectEventColumnLeftPercents = ({
  chartPoints,
  domainMax,
  domainMin,
}: {
  chartPoints?: LayoutChartPoint[];
  domainMax: number;
  domainMin: number;
}) =>
  (chartPoints ?? []).filter(isEventLayoutPoint).map((point) =>
    getTimestampPosition({
      maxTimestamp: domainMax,
      minTimestamp: domainMin,
      timestamp: point.timestamp,
    }),
  );

const tickPriority = (label: null | VestingGraphAxisTickLabel) => {
  if (label === 'GRANT_START') return 0;
  if (label === 'FULLY_VESTED') return 1;
  if (label === 'CLIFF') return 2;
  if (label === 'TODAY') return 3;

  return 4;
};

/** Drop the lower-priority tick of any remaining pair closer than one date label. */
const collapseOverlappingAxisTicks = (ticks: VestingGraphAxisTick[], minGapPercent: number) => {
  const ordered = [...ticks].sort((first, second) => first.leftPercent - second.leftPercent);
  const kept: VestingGraphAxisTick[] = [];

  ordered.forEach((tick) => {
    const previous = kept.at(-1);
    if (!previous || tick.leftPercent - previous.leftPercent >= minGapPercent) {
      kept.push(tick);
      return;
    }

    if (tickPriority(tick.label) < tickPriority(previous.label)) {
      kept[kept.length - 1] = tick;
    }
  });

  return kept;
};

/** True when two event columns sit closer than one date-tick of axis space. */
const doEventMarkersCollide = ({
  leftPercentA,
  leftPercentB,
  minGapPercent,
}: {
  leftPercentA: null | number;
  leftPercentB: null | number;
  minGapPercent: number;
}) => {
  if (leftPercentA == null || leftPercentB == null) return false;

  return Math.abs(leftPercentA - leftPercentB) < minGapPercent;
};

/**
 * When acceleration and expiry sit closer than one date-tick, stack the pills so both
 * stay readable. Dates live in the hover modal, not on the axis.
 */
export const resolveOverlappingEventMarkers = ({
  accelerationLeftPercent,
  expiryLeftPercent,
  minGapPercent,
}: {
  accelerationLeftPercent: null | number;
  expiryLeftPercent: null | number;
  minGapPercent: number;
}): {
  accelerationStack: VestingGraphEventMarkerStack;
  expiryStack: VestingGraphEventMarkerStack;
} => {
  if (
    accelerationLeftPercent == null ||
    expiryLeftPercent == null ||
    !doEventMarkersCollide({
      leftPercentA: accelerationLeftPercent,
      leftPercentB: expiryLeftPercent,
      minGapPercent,
    })
  ) {
    return {
      accelerationStack: 'center',
      expiryStack: 'center',
    };
  }

  const accelerationIsLeft = accelerationLeftPercent <= expiryLeftPercent;

  return {
    accelerationStack: accelerationIsLeft ? 'above' : 'below',
    expiryStack: accelerationIsLeft ? 'below' : 'above',
  };
};

export const buildVestingGraphAxisTicks = ({
  chartPoints,
  domainMax,
  domainMin,
  minGapPercent,
  segmentBounds,
  vestingGraph,
}: {
  chartPoints?: LayoutChartPoint[];
  domainMax: number;
  domainMin: number;
  minGapPercent: number;
  segmentBounds?: MilestoneSegmentBound[];
  vestingGraph: VestingGraphData;
}): VestingGraphAxisTick[] => {
  const placed: VestingGraphAxisTick[] = [];
  const eventLeftPercents = collectEventColumnLeftPercents({
    chartPoints,
    domainMax,
    domainMin,
  });
  const collidesWithEventColumn = (leftPercent: number) =>
    eventLeftPercents.some(
      (eventLeftPercent) => Math.abs(eventLeftPercent - leftPercent) < minGapPercent,
    );

  const pushTick = ({
    date,
    label,
    timestamp,
  }: {
    date: string;
    label: null | VestingGraphAxisTickLabel;
    timestamp: number;
  }) => {
    const leftPercent = getTimestampPosition({
      maxTimestamp: domainMax,
      minTimestamp: domainMin,
      timestamp,
    });

    // Skinny accel/expiry columns squeeze neighbouring payments closer than a date label.
    // Those dates live in the hover modal — keep them off the axis so they cannot overlap.
    if (
      collidesWithEventColumn(leftPercent) &&
      label !== 'GRANT_START' &&
      label !== 'FULLY_VESTED'
    ) {
      return;
    }

    const collideIndex = placed.findIndex(
      (tick) => Math.abs(tick.leftPercent - leftPercent) < minGapPercent,
    );
    if (collideIndex >= 0) {
      const existing = placed[collideIndex]!;

      // Today is only a cursor: a payment column keeps its payout date, not asOfDate.
      if (existing.label === 'TODAY' && !label) {
        placed.splice(collideIndex, 1);
      } else if (label === 'TODAY' && !existing.label) {
        return;
      } else if (!label || existing.label) {
        return;
      } else {
        placed.splice(collideIndex, 1);
      }
    }

    placed.push({ date, label, leftPercent });
  };

  if (
    vestingGraph.granularity === VestingGraphPointGranularity.MILESTONE &&
    segmentBounds?.length === vestingGraph.points.length
  ) {
    pushTick({
      date: vestingGraph.startDate,
      label: 'GRANT_START',
      timestamp: domainMin,
    });

    // Target charts caption Fully Vested on the last 100% milestone. A stale or earlier
    // fullVestingCalculatedDate must not steal the label into the middle of the axis.
    vestingGraph.points.forEach((point, index) => {
      const isLast = index === vestingGraph.points.length - 1;
      const captionFullyVested =
        isLast &&
        point.cumulativePercent >= 100 &&
        shouldCaptionFullyVested({
          accelerationDate: vestingGraph.accelerationDate,
          lastVestingDate: point.date,
        });
      pushTick({
        date: point.date,
        label: captionFullyVested ? 'FULLY_VESTED' : null,
        timestamp: segmentBounds[index]!.endTimestamp,
      });
    });

    return collapseOverlappingAxisTicks(placed, minGapPercent);
  }

  collectAxisTickCandidates(vestingGraph).forEach(({ date, label }) => {
    // If today snaps onto a column of another day, that column already has the payout date.
    // Event columns are not payouts — ignore them so today does not park under accel/expiry.
    if (label === 'TODAY' && chartPoints?.length) {
      const snappedIndex = findTimestampIndex(chartPoints, date, { ignoreEventColumns: true });
      if (snappedIndex != null) {
        const snappedCalendar = calendarTimeOf(chartPoints[snappedIndex]!);
        if (snappedCalendar !== new Date(date).getTime()) {
          return;
        }
      }
    }

    // Without a cliff the grant-start tick sits under its column. A cliff gutter is a
    // wide empty span, so the caption stays on the left edge of that span.
    if (label === 'GRANT_START' && chartPoints?.[0]) {
      const start = chartPoints[0];
      const startHalf = start.slotHalfWidth ?? 0;
      const grantStartTimestamp =
        vestingGraph.cliff && startHalf > 0 ? start.timestamp - startHalf : start.timestamp;
      pushTick({
        date,
        label,
        timestamp: grantStartTimestamp,
      });
      return;
    }

    if (label === 'FULLY_VESTED') {
      const lastVestingColumn = findLastVestingColumn(chartPoints);
      pushTick({
        date,
        label,
        timestamp:
          lastVestingColumn?.timestamp ??
          layoutTimestampForDate(chartPoints, date, { ignoreEventColumns: true }),
      });
      return;
    }

    pushTick({
      date,
      label,
      timestamp: layoutTimestampForDate(chartPoints, date, { ignoreEventColumns: true }),
    });
  });

  return collapseOverlappingAxisTicks(placed, minGapPercent);
};
